import { useState } from "react";
import { Sparkles, Loader2, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TYPES = [["conseil", "Article conseil"], ["faq", "FAQ"], ["local", "Page locale (métier + ville)"]];

export default function SeoTools() {
  const [type, setType] = useState("local");
  const [topic, setTopic] = useState("");
  const [metier, setMetier] = useState("Maçon");
  const [ville, setVille] = useState("");
  const [cp, setCp] = useState("");
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  const generate = async () => {
    if (type === "local" && (!metier || !ville)) { toast.error("Métier et ville sont requis"); return; }
    if (type !== "local" && !topic) { toast.error("Indiquez un sujet"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/admin/seo/generate", { type, topic, metier, ville, code_postal: cp, keywords });
      setResult(data);
      toast.success("Contenu généré par l'IA");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/articles", { ...result, published: true });
      toast.success("Contenu publié ! Visible sur le site.");
      setResult(null); setTopic(""); setVille(""); setCp("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setPublishing(false); }
  };

  const upd = (k) => (e) => setResult({ ...result, [k]: e.target.value });

  return (
    <div>
      <h1 className="font-heading text-3xl font-black text-ink">Générateur de contenu SEO</h1>
      <p className="text-ink/50 mt-1">Générez des pages puissantes optimisées pour le référencement grâce à l'IA (GPT-5.5).</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-6 space-y-4 h-fit">
          <div>
            <label className="text-xs font-semibold text-ink/60">Type de contenu</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {TYPES.map(([v, l]) => (
                <button key={v} onClick={() => setType(v)} data-testid={`seo-type-${v}`}
                  className={`text-xs font-semibold rounded-lg py-2 border transition ${type === v ? "bg-primary text-white border-primary" : "border-border hover:border-primary"}`}>{l}</button>
              ))}
            </div>
          </div>

          {type === "local" ? (
            <div className="grid grid-cols-1 gap-4">
              <div><label className="text-xs font-semibold text-ink/60">Métier</label><input data-testid="seo-metier" className={field} value={metier} onChange={(e) => setMetier(e.target.value)} placeholder="Ex : Maçon, Terrassier…" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-semibold text-ink/60">Ville</label><input data-testid="seo-ville" className={field} value={ville} onChange={(e) => setVille(e.target.value)} placeholder="Ex : Marignane" /></div>
                <div><label className="text-xs font-semibold text-ink/60">Code postal</label><input data-testid="seo-cp" className={field} value={cp} onChange={(e) => setCp(e.target.value)} placeholder="13700" /></div>
              </div>
            </div>
          ) : (
            <div><label className="text-xs font-semibold text-ink/60">Sujet</label><input data-testid="seo-topic" className={field} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={type === "faq" ? "Ex : la construction d'un mur de clôture" : "Ex : bien préparer sa dalle béton"} /></div>
          )}

          <div><label className="text-xs font-semibold text-ink/60">Mots-clés cibles (optionnel)</label><input className={field} value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="séparés par des virgules" /></div>

          <button onClick={generate} disabled={loading} data-testid="seo-generate" className="w-full inline-flex items-center justify-center gap-2 bg-ink text-white font-bold py-3 rounded-full hover:bg-primary transition disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />} Générer le contenu
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          {!result ? (
            <div className="h-full flex flex-col items-center justify-center text-ink/40 py-16"><Eye size={32} /><p className="mt-3 text-sm">L'aperçu du contenu généré s'affichera ici.</p></div>
          ) : (
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-ink/60">Titre</label><input className={field} value={result.title || ""} onChange={upd("title")} data-testid="seo-result-title" /></div>
              <div className="grid grid-cols-1 gap-3">
                <div><label className="text-xs font-semibold text-ink/60">Meta title</label><input className={field} value={result.meta_title || ""} onChange={upd("meta_title")} /></div>
                <div><label className="text-xs font-semibold text-ink/60">Meta description</label><textarea rows={2} className={field} value={result.meta_description || ""} onChange={upd("meta_description")} /></div>
                <div><label className="text-xs font-semibold text-ink/60">Extrait</label><textarea rows={2} className={field} value={result.excerpt || ""} onChange={upd("excerpt")} /></div>
              </div>
              <div><label className="text-xs font-semibold text-ink/60">Aperçu du contenu</label>
                <div className="mt-1 border border-border rounded-lg p-4 max-h-64 overflow-y-auto article-body bg-muted/30" dangerouslySetInnerHTML={{ __html: result.content }} />
              </div>
              <button onClick={publish} disabled={publishing} data-testid="seo-publish" className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full disabled:opacity-60">
                {publishing ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Publier sur le site
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
